import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TRAINING_DESCRIPTION_MIN_LENGTH, TRAINING_TASK_NAME } from '@app/shared/constants';
import { WorkLocation } from '@app/shared/enum/WorkLocation';
import { formatAndValidateTime } from '@app/shared/util/time-utils';
import { Day } from '@app/time-sheet/models/day';
import { SelectedCell } from '@app/time-sheet/models/selected-cell';
import { TimeSheetUpdate } from '@app/time-sheet/models/time-sheet-update';
import { TimeSheetChangeTrackerService } from '@app/time-sheet/service/time-sheet-change-tracker-service';
import { sumTimes, timeToTimeInputFormat } from '@app/time-sheet/util/time-sheet-utils';
import { AuthService } from '@auth/auth-service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';

type ExtraInfoField = 'startTime' | 'lunchLength' | 'totalHours' | 'description' | 'workLocation';

@Component({
  selector: 'abc-time-sheet-extra-info-card',
  imports: [FormsModule, DatePipe, NgbTooltipModule],
  templateUrl: 'time-sheet-extra-info-card.html',
  styleUrl: 'time-sheet-extra-info-card.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSheetExtraInfoCard {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly WorkLocation = WorkLocation;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly TRAINING_DESCRIPTION_MIN_LENGTH = TRAINING_DESCRIPTION_MIN_LENGTH;

  private changeTrackerService = inject(TimeSheetChangeTrackerService);
  private authService = inject(AuthService);

  selectedCell = input<SelectedCell | null>(null);
  extraInfoChange = output<{ field: ExtraInfoField; value: string | WorkLocation | null }>();

  isDisabled = computed(() => {
    const task = this.selectedCell()!.task;

    if (task.completed) return true;

    if (this.authService.isFreelancer() || this.authService.isImpersonating()) {
      return false;
    }

    return task.readOnly;
  });

  isTrainingTask = computed(() => this.selectedCell()?.task.taskName === TRAINING_TASK_NAME);

  descriptionPlaceholder = computed(() => {
    if (!this.isTrainingTask()) return '';
    return $localize`:placeholder|Placeholder text for training task description input@@trainingDescriptionPlaceholder:Training: [training name] - [description] - [certificate]`;
  });

  hasValidDescription = computed(() => {
    if (!this.isTrainingTask()) return true;

    const description = this.getSelectedCellValue('description') as string;
    return description != null && description.trim().length >= TRAINING_DESCRIPTION_MIN_LENGTH;
  });

  getSelectedCellValue(field: ExtraInfoField): string | WorkLocation | null {
    const cell = this.selectedCell();
    if (!cell) return '';

    const dateObj = new Date(cell.date);
    const tracked = this.changeTrackerService.getChange(cell.task, dateObj);

    if (tracked && field in tracked) {
      return tracked[field];
    }

    const dayOfMonth = dateObj.getDate();
    const serverDays = cell.task.days?.filter((d) => d.dayOfMonth === dayOfMonth) || [];

    if (serverDays.length > 0) {
      switch (field) {
        case 'totalHours': {
          const times = serverDays.map((d) => d.totalHours);
          return sumTimes(times);
        }
        case 'description': {
          const descriptions = serverDays
            .map((d) => d.description)
            .filter((d) => d != null && d.trim() !== '');
          return descriptions.join('\n');
        }
        case 'startTime': {
          const startTimes = serverDays.map((d) => d.startTime).filter((s) => s);
          return startTimes.length > 0 ? startTimes.sort()[0] : '';
        }
        case 'lunchLength': {
          const lunchLengths = serverDays.map((d) => d.lunchLength || '00:00');
          return sumTimes(lunchLengths);
        }
        case 'workLocation': {
          const locations = serverDays.map((d) => d.workLocation);
          // If all days are the same, return that; otherwise return MIXED
          const first = locations[0];
          return locations.every((l) => l === first) ? first : WorkLocation.Mixed;
        }
        default:
          return serverDays[0][field];
      }
    }

    return '';
  }

  onTimeBlur(field: 'startTime' | 'lunchLength', event: Event) {
    const input = event.target as HTMLInputElement;
    const formatted = formatAndValidateTime(input.value);

    if (formatted) {
      input.value = formatted;
    } else {
      input.value = '';
    }

    this.onExtraInfoChange(field, formatted);
  }

  onExtraInfoChange(field: ExtraInfoField, value: string | WorkLocation | null) {
    const cell = this.selectedCell();
    if (!cell) return;

    const dateObj = new Date(cell.date);
    const processedValue = value === '' ? null : value;

    const dayOfMonth = dateObj.getDate();
    const serverDays = cell.task.days?.filter((d) => d.dayOfMonth === dayOfMonth) || [];
    const serverEntry = this.mergeServerDays(serverDays, cell, dayOfMonth);

    this.changeTrackerService.updateChange(
      cell.task,
      dateObj,
      { [field]: processedValue },
      serverEntry
    );

    this.extraInfoChange.emit({ field, value: processedValue });
  }

  onEndTimeBlur(event: Event) {
    const input = event.target as HTMLInputElement;
    const formatted = formatAndValidateTime(input.value);

    if (formatted) {
      input.value = formatted;
      // Bereken total op basis van end time
      const calculatedTotal = this.calculateTotalFromEnd(formatted);
      if (calculatedTotal && calculatedTotal !== '00:00') {
        this.onExtraInfoChange('totalHours', calculatedTotal);
      }
    } else {
      input.value = '';
    }
  }

  private calculateTotalFromEnd(endTime: string): string {
    const cell = this.selectedCell();
    if (!cell) return '';

    const start = this.getSelectedCellValue('startTime') as string;
    const lunch = this.getSelectedCellValue('lunchLength') as string;

    if (!start || !lunch || !endTime) return '';

    const [hStart, mStart] = start.split(':').map(Number);
    const [hEnd, mEnd] = endTime.split(':').map(Number);
    const [hLunch, mLunch] = lunch.split(':').map(Number);

    // Total = End - Start - Lunch
    const totalMinutes = hEnd * 60 + mEnd - (hStart * 60 + mStart) - (hLunch * 60 + mLunch);

    const totalH = Math.floor(totalMinutes / 60);
    const totalM = totalMinutes % 60;

    return timeToTimeInputFormat(totalH, totalM);
  }

  private mergeServerDays(
    serverDays: Day[],
    cell: SelectedCell,
    dayOfMonth: number
  ): TimeSheetUpdate | null {
    if (serverDays.length === 0) return null;

    const first = serverDays[0];
    const totalHours = sumTimes(serverDays.map((d) => d.totalHours));
    const descriptions = serverDays
      .map((d) => d.description)
      .filter((d) => d != null && d.trim() !== '');
    const uniqueDescriptions = [...new Set(descriptions)];
    const description = uniqueDescriptions.join('; ');
    const startTimes = serverDays.map((d) => d.startTime).filter((s) => s);
    const startTime = startTimes.length > 0 ? startTimes.sort()[0] : first.startTime; // Earliest start
    const lunchLengths = serverDays.map((d) => d.lunchLength || '00:00');
    const lunchLength = sumTimes(lunchLengths);
    const locations = serverDays.map((d) => d.workLocation);
    const firstLocation = locations[0];
    const workLocation = locations.every((l) => l === firstLocation)
      ? firstLocation
      : WorkLocation.Mixed;

    return {
      projectId: cell.task.projectId,
      title: `${cell.task.customerName} - ${cell.task.taskName}`,
      customerName: cell.task.customerName,
      taskName: cell.task.taskName,
      dayOfMonth,
      totalHours,
      startTime,
      lunchLength,
      description,
      workLocation,
    };
  }

  calculateEndTime(): string {
    const cell = this.selectedCell();
    if (!cell) return '';
    const start = this.getSelectedCellValue('startTime') as string;
    const lunch = this.getSelectedCellValue('lunchLength') as string;
    const total = this.getSelectedCellValue('totalHours') as string;
    if (!start || !lunch || !total) return '';

    const [hStart, mStart] = start.split(':').map(Number);
    const [hTotal, mTotal] = total.split(':').map(Number);
    const [hLunch, mLunch] = lunch.split(':').map(Number);

    const endMinutes = hStart * 60 + mStart + (hTotal * 60 + mTotal) + (hLunch * 60 + mLunch);
    const endH = Math.floor((endMinutes / 60) % 24);
    const endM = endMinutes % 60;
    return timeToTimeInputFormat(endH, endM);
  }

  removeEntryFromChangeTracker() {
    const cell = this.selectedCell();
    if (!cell) return;

    if (cell.task.completed || cell.task.readOnly) {
      return;
    }

    this.changeTrackerService.removeChange(cell.task, new Date(cell.date));
  }

  removeEntryFromChangeTrackerDisabled = computed(() => {
    const cell = this.selectedCell();
    if (!cell) return true;

    if (this.changeTrackerService.getChange(cell.task, new Date(cell.date)) === null) {
      return true;
    }

    return cell.task.completed || cell.task.readOnly;
  });
}
